import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { updateRatesBase } from '@/api/tariffs'
import type { RatesBase, RatesBasePatch } from '@/types/tariffs'
import { SERVICE_ROWS, serviceRowLabel } from '@/types/tariffs'
import { InlineNumberInput } from './InlineNumberInput'

/**
 * §6.1 TARIFFS_SPEC.md — базовые тарифы.
 * Inline-редактирование 5 услуг × 2 колонки. Оптимистичное обновление.
 */
export function RatesBaseTable({ data }: { data: RatesBase }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [rowError, setRowError] = useState<Record<string, string>>({})
  const [busyField, setBusyField] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (patch: RatesBasePatch) => updateRatesBase(patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: ['tariffs', 'rates-base'] })
      const prev = qc.getQueryData<RatesBase>(['tariffs', 'rates-base'])
      if (prev) {
        qc.setQueryData<RatesBase>(['tariffs', 'rates-base'], { ...prev, ...patch })
      }
      return { prev }
    },
    onError: (err, patch, ctx) => {
      if (ctx?.prev) qc.setQueryData(['tariffs', 'rates-base'], ctx.prev)
      const field = Object.keys(patch)[0]
      if (field) {
        setRowError((s) => ({
          ...s,
          [field]: err instanceof Error ? err.message : t('tariffs.errorGeneric'),
        }))
      }
    },
    onSuccess: (_, patch) => {
      const field = Object.keys(patch)[0]
      if (field) {
        setRowError((s) => {
          if (!s[field]) return s
          const next = { ...s }
          delete next[field]
          return next
        })
      }
    },
    onSettled: () => {
      setBusyField(null)
      void qc.invalidateQueries({ queryKey: ['tariffs', 'rates-base'] })
    },
  })

  function commit(field: keyof RatesBase, next: number | null) {
    if (next == null || !Number.isFinite(next) || next < 0) return
    setBusyField(String(field))
    mutation.mutate({ [field]: next } as RatesBasePatch)
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-[color:var(--stryzh-color-border-default)]">
      <table className="w-full text-left text-sm">
        <thead className="bg-[color:var(--stryzh-color-surface)] text-[color:var(--stryzh-color-text-secondary)]">
          <tr>
            <th className="px-3 py-2">{t('tariffs.base.colService')}</th>
            <th className="px-3 py-2">{t('tariffs.base.colCustomer')}</th>
            <th className="px-3 py-2">{t('tariffs.base.colContractor')}</th>
          </tr>
        </thead>
        <tbody>
          {SERVICE_ROWS.map((row) => {
            const cField = row.customerField as keyof RatesBase
            const iField = row.inspectorField as keyof RatesBase
            const cVal = data[cField] as number
            const iVal = data[iField] as number
            const label = serviceRowLabel(row.code)
            return (
              <tr
                key={row.code}
                className="border-t border-[color:var(--stryzh-color-border-default)]"
              >
                <td className="px-3 py-2 font-medium">{label}</td>
                <td className="px-3 py-2">
                  <InlineNumberInput
                    value={cVal}
                    onCommit={(v) => commit(cField, v)}
                    busy={busyField === String(cField) && mutation.isPending}
                    min={0}
                    width={120}
                    ariaLabel={t('tariffs.base.ariaCustomer', { service: label })}
                    invalid={Boolean(rowError[String(cField)])}
                  />
                  {rowError[String(cField)] ? (
                    <div className="stryzh-input-error-text mt-1">{rowError[String(cField)]}</div>
                  ) : null}
                </td>
                <td className="px-3 py-2">
                  <InlineNumberInput
                    value={iVal}
                    onCommit={(v) => commit(iField, v)}
                    busy={busyField === String(iField) && mutation.isPending}
                    min={0}
                    width={120}
                    ariaLabel={t('tariffs.base.ariaContractor', { service: label })}
                    invalid={Boolean(rowError[String(iField)])}
                  />
                  {rowError[String(iField)] ? (
                    <div className="stryzh-input-error-text mt-1">{rowError[String(iField)]}</div>
                  ) : null}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
